<script setup lang="ts">
import {Money} from "../../../utils/money.js";
import ImageWithFallback from "../ImageWithFallback.vue";
import {useCartStore} from "../../stores/cart.js";
import type {CartCalculatorLineResponse} from "../../../responses/Site/CartCalculatorLineResponse.ts";
import {toast} from "vue-sonner";
import {label} from "../../mixins/translations.ts";
import {Trash2Icon} from "lucide-vue-next";
import CartPriceLoadingSkeleton from "./CartPriceLoadingSkeleton.vue";
import CustomNumberInput from "../CustomNumberInput.vue";
import {onMounted, ref, type Ref, watch} from "vue";

const cartStore = useCartStore();

interface Props {
    item: CartCalculatorLineResponse
    canEdit?: boolean
}

const props = withDefaults<Props>(defineProps<Props>(), {
    canEdit: true
})

const quantity: Ref<number> = ref(props.item.model.quantity);

const removeItem = async () => {
    await cartStore.destroyLine({variant_id: props.item.model.item.id})
        .then((response) => {
            toast.success(response.data.message)
        }).catch((response) => {
            toast.error(label('site.cart.item.deleted.error'))
        })
}


const updateQuantity = async (quantity) => {
    if(quantity < 1) {
        removeItem();
    } else {
        await cartStore.updateQuantity({
            cart_item_id: props.item.model.id,
            quantity: quantity,
        }).then((response) => {
            toast.success(label('site.cart.item.update'))
        }).catch((response) => {
            toast.error(label('site.cart.item.update.error'))
        })
    }
}

const handleAmountChange = (newAmount) => {
    quantity.value = newAmount;
    updateQuantity(quantity.value);
}

</script>
<template>
    <li class="flex items-center space-x-4 px-4 py-6 sm:px-6">
        <div class="relative flex shrink-0 border border-gray-200 rounded-md">
            <ImageWithFallback
                :src="item.model.item.product?.thumbnail?.image_path"
                :alt="item.model.item.name"
                class="h-20 w-20 rounded-md"
            />
        </div>
        <div class="ml-6 flex flex-col flex-auto flex-1">
            <h4 class="line-clamp-2">
                <a :href="`/${item.model.item?.product?.slug}/`" class="font-semibold text-gray-700 hover:text-gray-800">
                    {{ item.model.item.name }}
                </a>
            </h4>
            <ul class="flex divide-x divide-gray-200 text-sm text-gray-500">
                <li class="inline">{{ item.model.item.ean }}</li>
            </ul>
            <ul class="w-full flex mt-2 gap-1" v-if="canEdit">
                <li>
                    <button type="button" @click.prevent="removeItem" class="h-7 w-7 flex justify-center items-center border rounded-sm border-gray-200 text-gray-500 hover:bg-error-500 hover:text-error-50">
                        <Trash2Icon class="w-4 h-4 flex"></Trash2Icon>
                    </button>
                </li>
            </ul>
        </div>
        <div class="flex" v-if="canEdit">
            <custom-number-input size="small" :value="quantity" @input="handleAmountChange"></custom-number-input>
        </div>
        <p class="font-medium flex flex-col text-right space-y-1">
            <CartPriceLoadingSkeleton>{{ Money.format(item.price_excl, item.currency) }}</CartPriceLoadingSkeleton>
        </p>
    </li>
</template>
